import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import DateUtils from '../utils/DateUtils';

flatpickr.localize(Italian);

function initOne(root) {
	const fieldId = root.getAttribute('data-field-id');
	const dateFormat = root.getAttribute('data-date-format') || 'd.m.Y';

	/** Shorter label on the closed trigger (2-digit year) so the range fits narrow layouts */
	function getLabelDisplayFormat() {
		return dateFormat.replace(/Y/g, 'y');
	}

	const hiddenInput = root.querySelector(`input[name="${fieldId}"]`);
	const hiddenInputFrom = root.querySelector(`input[name="${fieldId}_from"]`);
	const hiddenInputTo = root.querySelector(`input[name="${fieldId}_to"]`);
	const triggerBtn = root.querySelector(`#${fieldId}-trigger`);
	const labelSpan = root.querySelector('.period-datepicker-label');
	const rangePanel = root.querySelector('.period-datepicker-range');
	const rangeInput = root.querySelector('.period-datepicker-range input');
	const presetButtons = root.querySelectorAll('.period-datepicker-preset');
	const customChoiceBtn = root.querySelector('[data-period-custom-choice]');
	const dropdownMenu = root.querySelector('.dropdown-menu');

	if (!hiddenInput || !hiddenInputFrom || !hiddenInputTo || !rangeInput || !triggerBtn || !dropdownMenu || !rangePanel) {
		return;
	}

	const periodDropdownViewportMargin = 8;

	let fp = null;
	/** When user selects a range manually in the calendar, keep it so we restore on reopen */
	let lastManualRange = null;
	/** While dropdown is open: show inline calendar only for custom range or after "A scelta" */
	let calendarPanelVisible = false;

	function dateToYmd(date) {
		const d = new Date(date);
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${day}`;
	}

	/**
	 * Flatpickr range uses calendar days; mixed times (e.g. preset "today" 00:00–23:59) can leave a half-selected range.
	 * Normalize to local noon for stable day identity.
	 */
	function toCalendarDayDate(date) {
		const d = new Date(date);
		if (Number.isNaN(d.getTime())) {
			return d;
		}
		return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
	}

	/** Reset picker state, then set a full range (or clear). Does not fire onChange. */
	function applyPickerRange(instance, range) {
		if (!instance) {
			return;
		}
		if (!range || range.length !== 2) {
			instance.clear();
			return;
		}
		let start = toCalendarDayDate(range[0]);
		let end = toCalendarDayDate(range[1]);
		if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
			instance.clear();
			return;
		}
		if (start.getTime() > end.getTime()) {
			[start, end] = [end, start];
		}
		instance.clear();
		instance.setDate([start, end], false);
	}

	function showPreviousAndCurrentMonth(instance) {
		if (!instance) {
			return;
		}
		const previousMonth = new Date();
		previousMonth.setDate(1);
		previousMonth.setMonth(previousMonth.getMonth() - 1);
		instance.jumpToDate(previousMonth, false);
	}

	function alignViewToRangeEndOnRight(instance, range) {
		if (!instance || !range || range.length !== 2) {
			return;
		}
		const endDate = toCalendarDayDate(range[1]);
		if (Number.isNaN(endDate.getTime())) {
			return;
		}
		const leftMonth = new Date(endDate.getFullYear(), endDate.getMonth() - 1, 1, 12, 0, 0, 0);
		instance.jumpToDate(leftMonth, false);
	}

	/** True when form uses explicit from/to without a period preset key */
	function isCustomDateRange() {
		const periodVal = (hiddenInput.value || '').trim();
		const fromVal = (hiddenInputFrom.value || '').trim();
		const toVal = (hiddenInputTo.value || '').trim();
		return !periodVal && fromVal && toVal;
	}

	/** Format YYYY-MM-DD to display format for the trigger label (2-digit year when base format uses Y) */
	function ymdToLabel(ymd) {
		if (!ymd) return '';
		const [y, m, d] = ymd.split('-').map(Number);
		if (!y || !m || !d) return ymd;
		const pad = (n) => String(n).padStart(2, '0');
		const labelFmt = getLabelDisplayFormat();
		return labelFmt.replace('Y', y).replace('y', String(y).slice(-2)).replace('m', pad(m)).replace('d', pad(d));
	}

	function setCalendarPanelVisible(visible) {
		calendarPanelVisible = visible;
		rangePanel.classList.toggle('d-none', !visible);
		if (visible && fp) {
			requestAnimationFrame(() => {
				fp.redraw();
			});
		}
	}

	function refreshActiveStates() {
		const periodVal = (hiddenInput.value || '').trim();
		const custom = isCustomDateRange();
		presetButtons.forEach((btn) => {
			const match = btn.getAttribute('data-preset') === periodVal;
			btn.classList.toggle('active', match && periodVal !== '' && !calendarPanelVisible);
		});
		if (customChoiceBtn) {
			customChoiceBtn.classList.toggle('active', calendarPanelVisible || custom);
		}
	}

	/** Set label from current hidden inputs (preset name or from–to range). Call on init and when state changes. */
	function syncLabelFromInputs() {
		const periodVal = (hiddenInput.value || '').trim();
		const fromVal = (hiddenInputFrom.value || '').trim();
		const toVal = (hiddenInputTo.value || '').trim();
		if (periodVal) {
			const presetBtn = root.querySelector(`.period-datepicker-preset[data-preset="${periodVal}"]`);
			labelSpan.textContent = presetBtn ? presetBtn.textContent.trim() : periodVal;
			return;
		}
		if (fromVal && toVal) {
			labelSpan.textContent = fromVal === toVal ? ymdToLabel(fromVal) : `${ymdToLabel(fromVal)} – ${ymdToLabel(toVal)}`;
			return;
		}
		const defaultBtn = root.querySelector('.period-datepicker-preset');
		if (defaultBtn) {
			labelSpan.textContent = defaultBtn.textContent.trim();
		}
	}

	function setActivePreset(preset) {
		const presetBtn = root.querySelector(`.period-datepicker-preset[data-preset="${preset}"]`);
		labelSpan.textContent = presetBtn ? presetBtn.textContent.trim() : preset;
		hiddenInput.value = preset;
		hiddenInputFrom.value = '';
		hiddenInputTo.value = '';
		setCalendarPanelVisible(false);
		refreshActiveStates();
	}

	function setLabelFromRange(dates, instance) {
		if (!dates || dates.length < 2 || !instance) return;
		const labelFmt = getLabelDisplayFormat();
		const startLabel = instance.formatDate(dates[0], labelFmt);
		const endLabel = instance.formatDate(dates[1], labelFmt);
		const sameDay = dateToYmd(dates[0]) === dateToYmd(dates[1]);
		labelSpan.textContent = sameDay ? startLabel : `${startLabel} – ${endLabel}`;
	}

	function openFlatpickr() {
		if (fp) {
			return fp;
		}
		if (rangePanel.classList.contains('d-none')) {
			return null;
		}
		fp = flatpickr(rangeInput, {
			mode: 'range',
			dateFormat: dateFormat,
			time_24hr: true,
			allowInput: false,
			showMonths: 2,
			inline: true,
			changeYear: true,
			maxDate: 'today',
			disable: [
				function (date) {
					return date > new Date();
				},
			],
			onClose() {
				dropdownMenu.classList.remove('show');
				root.classList.remove('show');
			},
			onChange(selectedDates, _dateStr, instance) {
				if (selectedDates.length === 2) {
					lastManualRange = selectedDates.slice();
					setLabelFromRange(lastManualRange, instance);
					hiddenInput.value = '';
					hiddenInputFrom.value = dateToYmd(selectedDates[0]);
					hiddenInputTo.value = dateToYmd(selectedDates[1]);
					calendarPanelVisible = true;
					refreshActiveStates();
				}
			},
			onReady(_selectedDates, _dateStr, instance) {
				const calendarEl = instance.calendarContainer;
				if (calendarEl) {
					calendarEl.addEventListener('click', (e) => e.stopPropagation());
				}
			},
		});
		return fp;
	}

	function syncPresetOnlyOpenState() {
		const preset = (hiddenInput.value || '').trim();
		if (preset) {
			hiddenInputFrom.value = '';
			hiddenInputTo.value = '';
			lastManualRange = null;
		}
	}

	// On dropdown show: calendar only if custom range or will be revealed via "A scelta"
	$(root).on('show.bs.dropdown', function () {
		const opening_custom = isCustomDateRange();
		const calendar_was_hidden = rangePanel.classList.contains('d-none');
		calendarPanelVisible = opening_custom;
		setCalendarPanelVisible(calendarPanelVisible);

		if (!calendarPanelVisible) {
			syncPresetOnlyOpenState();
			refreshActiveStates();
			return;
		}

		if (fp && calendar_was_hidden) {
			fp.destroy();
			fp = null;
		}

		const preset = (hiddenInput.value || '').trim();
		const range = lastManualRange ?? DateUtils.getRangeForPeriod(preset);
		openFlatpickr();
		if (!fp) {
			refreshActiveStates();
			return;
		}
		applyPickerRange(fp, range);
		if (lastManualRange && range && range.length === 2) {
			alignViewToRangeEndOnRight(fp, range);
			setLabelFromRange(lastManualRange, fp);
		}
		refreshActiveStates();
	});

	// Override Popper: fixed position + horizontal clamp so wide calendar stays inside the viewport
	function applyFixedPosition() {
		const rect = triggerBtn.getBoundingClientRect();
		const topPx = rect.bottom;
		root.style.setProperty('--period-dropdown-top', `${topPx}px`);

		const menuRect = dropdownMenu.getBoundingClientRect();
		let menuWidth = menuRect.width;
		if (menuWidth < 4) {
			menuWidth = dropdownMenu.offsetWidth;
		}
		if (menuWidth < 4) {
			menuWidth = 580;
		}

		let leftPx = rect.right - menuWidth;
		const minLeft = periodDropdownViewportMargin;
		const maxLeft = window.innerWidth - periodDropdownViewportMargin - menuWidth;
		if (maxLeft < minLeft) {
			leftPx = minLeft;
		} else {
			leftPx = Math.min(Math.max(minLeft, leftPx), maxLeft);
		}

		root.style.setProperty('--period-dropdown-left', `${leftPx}px`);
		dropdownMenu.style.setProperty('position', 'fixed', 'important');
		dropdownMenu.style.setProperty('top', `${topPx}px`, 'important');
		dropdownMenu.style.setProperty('left', `${leftPx}px`, 'important');
		dropdownMenu.style.setProperty('right', 'auto', 'important');
		dropdownMenu.style.setProperty('transform', 'none', 'important');
		dropdownMenu.style.setProperty('will-change', 'auto', 'important');
	}

	$(root).on('shown.bs.dropdown', function () {
		applyFixedPosition();
		requestAnimationFrame(function () {
			applyFixedPosition();
			setTimeout(applyFixedPosition, 0);
		});
		if (calendarPanelVisible && fp) {
			requestAnimationFrame(() => fp.redraw());
		}
	});

	if (customChoiceBtn) {
		customChoiceBtn.addEventListener('click', function (e) {
			e.preventDefault();
			e.stopPropagation();
			const calendar_was_hidden = rangePanel.classList.contains('d-none');
			calendarPanelVisible = true;
			setCalendarPanelVisible(true);
			if (fp && calendar_was_hidden) {
				fp.destroy();
				fp = null;
			}
			openFlatpickr();
			if (!fp) {
				refreshActiveStates();
				requestAnimationFrame(() => {
					applyFixedPosition();
				});
				return;
			}
			if (lastManualRange && lastManualRange.length === 2) {
				applyPickerRange(fp, lastManualRange);
				alignViewToRangeEndOnRight(fp, lastManualRange);
				setLabelFromRange(lastManualRange, fp);
			} else {
				applyPickerRange(fp, null);
				showPreviousAndCurrentMonth(fp);
			}
			refreshActiveStates();
			requestAnimationFrame(() => {
				applyFixedPosition();
				if (fp) {
					fp.redraw();
				}
			});
		});
	}

	// Preset click: compute range, set in picker, clear manual selection (period set, period_from/to cleared)
	presetButtons.forEach((btn) => {
		btn.addEventListener('click', function (e) {
			e.preventDefault();
			lastManualRange = null;
			const preset = this.getAttribute('data-preset');
			const range = DateUtils.getRangeForPeriod(preset);
			const calendar_column_visible = !rangePanel.classList.contains('d-none');
			if (calendar_column_visible) {
				openFlatpickr();
				if (fp) {
					applyPickerRange(fp, range);
				}
			} else if (fp) {
				fp.destroy();
				fp = null;
			}
			setActivePreset(preset);
			$(triggerBtn).dropdown('hide');
			$(hiddenInput).trigger('change');
		});
	});

	// Restore lastManualRange from URL/form when period_from and period_to are set (e.g. after page reload)
	const fromValInit = (hiddenInputFrom.value || '').trim();
	const toValInit = (hiddenInputTo.value || '').trim();
	if (fromValInit && toValInit && !(hiddenInput.value || '').trim()) {
		const fromDate = new Date(fromValInit + 'T00:00:00');
		const toDate = new Date(toValInit + 'T00:00:00');
		if (!Number.isNaN(fromDate.getTime()) && !Number.isNaN(toDate.getTime())) {
			lastManualRange = [fromDate, toDate];
		}
	}

	syncLabelFromInputs();
	setCalendarPanelVisible(isCustomDateRange());
	refreshActiveStates();
}

/**
 * Initialize all period-datepicker dropdowns on the page.
 * Call once on DOM ready (e.g. from custom.js).
 */
export function initPeriodDatepickerFilters() {
	document.querySelectorAll('[data-period-datepicker]').forEach(initOne);
}
